import type { Permission } from "@/core/auth/usePermissions";

import { kioskRoleFor, type KioskRole } from "./kioskRoles";

/**
 * Les rôles en kiosque encore refusés sur un vrai téléphone.
 *
 * Ni le chatteur ni le partenaire n'y sont plus (28-08-2026). Ajouter ou
 * retirer un rôle ici est une décision de Kayla — c'est la seule ligne.
 *
 * 🩸 Le partenaire est sorti d'ici parce que c'est LUI qu'on visait depuis le
 * début, et qu'on l'a cru chatteur. Kayla a montré la capture d'un compte
 * bloqué en disant « je veux qu'il puisse bosser depuis son téléphone » ; j'ai
 * lu « chatteur » sans le vérifier, sorti le chatteur, et l'intéressé est resté
 * dehors. En base : Ali Alidjan → rôle `partenaire` (« Partenaire site »), UNE
 * permission, `sites.view`. Il ne chatte pas du tout. Un rôle se LIT, il ne se
 * devine pas d'un écran de blocage — l'écran est le même pour tous.
 */
export const ROLES_SANS_TELEPHONE: readonly KioskRole[] = ["manager"];

/**
 * Décision d'accès au shell sur mobile.
 *
 * Règle produit : **certains rôles en kiosque ne peuvent pas utiliser le
 * cockpit sur un vrai téléphone.** Leur poste est une barre d'onglets fixe
 * pensée pour un écran d'ordinateur, et ils n'ont ni taskbar ni liste des écrans
 * du Bureau pour s'en sortir autrement. Les rôles qui gardent le Bureau (CEO, Operator,
 * VA) ont toujours eu l'accès mobile.
 *
 * 🪤 L'histoire de cette liste, parce qu'elle a changé deux fois :
 *   · elle a d'abord visé le seul CHATTEUR (le chat était desktop-only) ;
 *   · le 26-08-2026, Kayla sur le manager chatter : « bloque-le sur mobile
 *     aussi » — le gate est alors passé sur `kioskRoleFor`, donc TOUT kiosque ;
 *   · le 28-08-2026, Kayla sur un compte bloqué depuis son iPhone : « je veux
 *     qu'il puisse bosser depuis son téléphone. » Le chatteur ressort — puis le
 *     PARTENAIRE, qui était en fait le compte en question (rôle lu en base
 *     après coup). Il ne reste que le manager chatter.
 *
 * D'où une liste EXPLICITE plutôt qu'un `kioskRoleFor` non nul : les deux
 * notions ont divergé, et les confondre a coûté un aller-retour. Perdre le
 * Bureau et perdre le téléphone ne sont plus la même chose.
 *
 * Logique pure isolée ici (testable sans React) — cf `wizardGate.ts` /
 * `shiftGateMath.ts` pour le même pattern.
 *
 *   - `"allow"`   → rendre le shell normal (desktop, ou mobile hors kiosque).
 *   - `"loading"` → permissions pas encore résolues sur mobile : rendre un
 *                   canvas neutre pour NE PAS flasher les conversations à
 *                   quelqu'un avant de connaître son rôle.
 *   - `"block"`   → mobile + kiosque : écran « va sur ton ordinateur ».
 */
export type MobileGateDecision = "allow" | "loading" | "block";

export function resolveMobileGate(params: {
  /**
   * 🩸 L'APPAREIL, pas le shell rendu. `?mobile=0` sur un téléphone rend le
   * shell desktop ; si le gate lisait ça, un chatteur passerait — la porte
   * dérobée ouverte le 28-08-2026 par le forçage de rendu. La règle de Kayla
   * porte sur le matériel (« va sur ton ordinateur »), donc on lit le matériel.
   */
  isMobile: boolean;
  permsLoading: boolean;
  roleSlug: string | null;
  permissions: Set<Permission> | null;
}): MobileGateDecision {
  // Desktop : jamais bloqué, on n'attend même pas les permissions.
  if (!params.isMobile) return "allow";
  // Mobile mais rôle encore inconnu : neutre (anti-flash).
  if (params.permsLoading) return "loading";
  // Mobile + rôle de la liste : bloqué.
  const kiosque = kioskRoleFor(params.roleSlug, params.permissions);
  if (kiosque !== null && ROLES_SANS_TELEPHONE.includes(kiosque)) return "block";
  // Tout le reste — dont le CHATTEUR depuis le 28-08 : accès normal.
  return "allow";
}
